import { useQuery, UseQueryResult } from '@tanstack/react-query'
import APIService from 'api/APIService'
import { useMemo } from 'react'
import dayjs from 'utils/dayjs'
import { useSettings } from 'core/hooks/settings/useSettings'
import { RoadSmartVideo } from 'types/motus'

interface RoadSmartVideosResponse {
  videos: RoadSmartVideo[]
}

interface UseRoadSmartVideosReturn extends Omit<
  UseQueryResult<RoadSmartVideosResponse>,
  'data' | 'isLoading' | 'isFetching'
> {
  /** Array of all Road Smart videos */
  videos: RoadSmartVideo[]
  /** Whether there are any overdue videos (status is 'overdue' or due date has passed) */
  hasOverdueVideos: boolean
  /** Videos with status 'assigned' or 'overdue' */
  toDoVideos: RoadSmartVideo[]
  /** Videos with status 'complete' */
  completedVideos: RoadSmartVideo[]
  /** Whether the query is currently loading */
  isLoading: boolean
  /** Whether the query is currently fetching (includes background refetches) */
  isFetching: boolean
}

/**
 * Hook to fetch and manage Road Smart training videos
 *
 * Fetches the list of Road Smart videos from the API and provides computed lists
 * for to-do videos, completed videos, and overdue status. The query is only enabled
 * for Motus driver experience users.
 *
 * The hook includes a 60-second stale time, meaning the data will be considered fresh
 * for 60 seconds before refetching.
 *
 * @returns {UseRoadSmartVideosReturn} Object containing video lists and query state
 *
 * @example
 * ```tsx
 * const { toDoVideos, completedVideos, hasOverdueVideos, isLoading } = useRoadSmartVideos()
 *
 * if (hasOverdueVideos) {
 *   // Show overdue banner
 * }
 *
 * return <VideoList videos={toDoVideos} />
 * ```
 */
const useRoadSmartVideos = (): UseRoadSmartVideosReturn => {
  const {
    data: {
      featureFlags: { ENABLE_DRIVER_EXPERIENCE }
    }
  } = useSettings()

  const { data, ...opts } = useQuery<RoadSmartVideosResponse>({
    queryKey: ['roadsmart', 'videos'],
    queryFn: APIService.motus.getRoadSmartVideos,
    staleTime: 60 * 1000,
    enabled: ENABLE_DRIVER_EXPERIENCE
  })

  /**
   * Check if there are any overdue videos.
   * A video is considered overdue if:
   * 1. The status is explicitly set to 'overdue' by the backend, OR
   * 2. The due date has passed (current date is same or after due date)
   * This dual check ensures we catch videos that may not have been updated by the backend yet.
   */
  const hasOverdueVideos = useMemo(() => {
    return (
      data?.videos.some((v) => {
        return (
          v.status === 'overdue' || dayjs().isSameOrAfter(v.due_date, 'day')
        )
      }) ?? false
    )
  }, [data?.videos])

  const toDoVideos = useMemo(() => {
    return (
      data?.videos?.filter((video) => {
        return video.status === 'assigned' || video.status === 'overdue'
      }) ?? []
    )
  }, [data?.videos])

  const completedVideos = useMemo(() => {
    return (
      data?.videos?.filter((video) => {
        return video.status === 'complete'
      }) ?? []
    )
  }, [data?.videos])

  return {
    videos: data?.videos ?? [],
    hasOverdueVideos,
    toDoVideos,
    completedVideos,
    ...opts
  }
}

export default useRoadSmartVideos
